import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Landing page for the SAML SSO callback redirect.
 *
 * The backend redirects here with:
 *   /sso-callback?token=<JWT>&user=<URLencoded JSON>
 *
 * On error the backend redirects to:
 *   /login?error=sso_failed
 */
export default function SSOCallback() {
  const [error, setError]        = useState(null);
  const [searchParams]           = useSearchParams();
  const { loginWithToken, user } = useAuth();
  const hasRun                   = useRef(false);

  useEffect(() => {
    // Run once per mount. React 18 StrictMode (active in the dev build QA serves)
    // double-invokes effects; the guard keeps the token handoff idempotent.
    if (hasRun.current) return;
    hasRun.current = true;

    const token     = searchParams.get('token');
    const userParam = searchParams.get('user');
    const errParam  = searchParams.get('error');

    if (errParam) {
      setError('Sign-in failed. Please try again or contact your administrator.');
      return;
    }
    if (!token) {
      setError('No token received from SSO provider. Please try again.');
      return;
    }

    try {
      const parsedUser = userParam ? JSON.parse(decodeURIComponent(userParam)) : null;

      // Store the JWT + user and set auth state. We deliberately DO NOT do a hard
      // window.location navigation here.
      //
      // History: the previous code did `window.location.replace('/dashboard')`
      // immediately after writing the token to localStorage. On iOS Safari/WebKit
      // a localStorage write is NOT reliably flushed to the persistent store before
      // a full-document navigation tears the page down — so the freshly-loaded
      // /dashboard document booted token-less, bounced to /login, and re-fired SSO.
      // That is the infinite loop seen on mobile (desktop flushes fast enough to
      // hide it). Reading the value back on THIS page does not prove cross-document
      // durability, so a read-back guard could not fix it either.
      //
      // Instead we set auth state and navigate CLIENT-SIDE (the <Navigate> below),
      // staying in the same document. We never depend on the write surviving a
      // document reload, and the first /auth/me request reads the token from the
      // same in-memory localStorage.
      loginWithToken(token, parsedUser);
    } catch (e) {
      console.error('SSOCallback parse error:', e);
      setError('Failed to process SSO response. Please try again.');
    }
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-white font-semibold text-lg mb-2">Sign-in failed</h2>
          <p className="text-slate-400 text-sm mb-6">{error}</p>
          <a
            href="/login"
            className="inline-block bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors"
          >
            Back to Login
          </a>
        </div>
      </div>
    );
  }

  // Once AuthContext has committed the user (set synchronously by loginWithToken
  // from the SSO payload), navigate client-side. Rendering <Navigate> only AFTER
  // `user` is truthy avoids the setUser-vs-ProtectedRoute race the old hard
  // navigation was working around — ProtectedRoute already sees the user, and the
  // pending /auth/me call (kicked off by AuthContext) confirms the token.
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-400 text-sm">Completing sign-in…</p>
      </div>
    </div>
  );
}
